const VALID_MODES = ['day', 'night'];

const readCookie = (name) => {
    const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const match = document.cookie.match(new RegExp(`(?:^|; )${escaped}=([^;]*)`));
    return match ? decodeURIComponent(match[1]) : null;
};

const saveMode = (mode, cookieName) => {
    try {
        window.localStorage.setItem(cookieName, mode);
    } catch (error) {
        // Cookies still preserve the user's preference when localStorage is blocked.
    }

    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `${cookieName}=${mode}; Max-Age=31536000; Path=/; SameSite=Lax${secure}`;
};

export function initThemeMode() {
    const config = window.LED_THEME || {};
    const cookieName = config.colorModeCookie || 'led_color_mode';
    const fallback = VALID_MODES.includes(config.defaultColorMode) ? config.defaultColorMode : 'day';
    const cookieMode = readCookie(cookieName);
    const root = document.documentElement;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let mode = VALID_MODES.includes(root.dataset.theme)
        ? root.dataset.theme
        : (VALID_MODES.includes(cookieMode) ? cookieMode : fallback);
    let transitionInFlight = null;

    const toggles = [...document.querySelectorAll('[data-theme-toggle]')];

    const applyMode = (nextMode, persist = false) => {
        mode = VALID_MODES.includes(nextMode) ? nextMode : fallback;
        root.dataset.theme = mode;
        root.style.colorScheme = mode === 'night' ? 'dark' : 'light';

        toggles.forEach((toggle) => {
            toggle.dataset.mode = mode;
            toggle.setAttribute('aria-pressed', String(mode === 'night'));
        });

        if (persist) {
            saveMode(mode, cookieName);
        }

        window.dispatchEvent(new CustomEvent('led:themechange', {
            detail: { mode, animated: Boolean(root.dataset.themeTransitioning) },
        }));
    };

    const render = (nextMode, persist = false, animate = false) => {
        const normalized = VALID_MODES.includes(nextMode) ? nextMode : fallback;

        if (normalized === mode && root.dataset.theme === normalized) {
            applyMode(normalized, persist);
            return Promise.resolve();
        }

        if (!animate || reducedMotion) {
            applyMode(normalized, persist);
            return Promise.resolve();
        }

        if (transitionInFlight) return transitionInFlight;

        root.dataset.themeTransitioning = 'true';

        if (typeof document.startViewTransition === 'function') {
            const transition = document.startViewTransition(() => {
                applyMode(normalized, persist);
            });

            transitionInFlight = transition.finished
                .catch(() => undefined)
                .finally(() => {
                    delete root.dataset.themeTransitioning;
                    transitionInFlight = null;
                    window.dispatchEvent(new CustomEvent('led:themetransitionend', { detail: { mode } }));
                });

            return transitionInFlight;
        }

        root.dataset.themeFallbackTransition = 'true';
        applyMode(normalized, persist);

        transitionInFlight = new Promise((resolve) => {
            window.setTimeout(resolve, 860);
        }).finally(() => {
            delete root.dataset.themeFallbackTransition;
            delete root.dataset.themeTransitioning;
            transitionInFlight = null;
            window.dispatchEvent(new CustomEvent('led:themetransitionend', { detail: { mode } }));
        });

        return transitionInFlight;
    };

    toggles.forEach((toggle) => {
        toggle.addEventListener('click', () => {
            render(mode === 'day' ? 'night' : 'day', true, true);
        });
    });

    applyMode(mode);

    return {
        get mode() {
            return mode;
        },
        set: (nextMode) => render(nextMode, true, true),
    };
}
